import type { Profile, UpdateProfile } from '@secretario/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from './api/http.js';
import { getProfile, patchProfile } from './api/profile.js';
import { readCachedProfile, writeCachedProfile } from './profileCache.js';
import { readSession } from './session.js';

export const PROFILE_KEY = ['profile'] as const;

async function fetchProfile(): Promise<Profile> {
  try {
    const profile = await getProfile();
    writeCachedProfile(readSession()?.user.id, profile);
    return profile;
  } catch (error) {
    // Sem rede (ou servidor fora): abre com o perfil guardado, para o que funciona no aparelho
    // (treino em andamento, rascunhos) seguir funcionando. Recusa de verdade (4xx) não passa.
    const offline = !(error instanceof ApiError) || error.status === 0 || error.status >= 500;
    const cached = offline ? readCachedProfile(readSession()?.user.id) : null;
    if (cached) return cached;
    throw error;
  }
}

/**
 * O perfil da pessoa. Com o último guardado no aparelho, a tela já abre com ele
 * (`isPlaceholderData` enquanto o servidor não confirma); sem nada guardado, espera.
 */
export function useProfile(enabled = true) {
  return useQuery({
    queryKey: PROFILE_KEY,
    queryFn: fetchProfile,
    staleTime: 30_000,
    enabled,
    placeholderData: () => readCachedProfile(readSession()?.user.id) ?? undefined,
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: UpdateProfile) => patchProfile(patch),
    onSuccess: (profile: Profile) => {
      queryClient.setQueryData(PROFILE_KEY, profile);
      writeCachedProfile(readSession()?.user.id, profile);
    },
  });
}
